// 网络出口：公网页面加载后探测内网直连地址（lan.*），可达则 API / WebSocket / 实时画面 / 证据全部改走内网
import { useSyncExternalStore } from "react";

declare global {
  interface Window {
    CSP_LAN_ORIGIN?: string | null;
  }
}

export type NetMode = "lan" | "public";

type FetchLike = (input: string, init?: RequestInit) => Promise<{ ok: boolean; json: () => Promise<unknown> }>;

/** 去掉末尾斜杠；空串视为未配置 */
export function normalizeOrigin(o: string | null | undefined): string | null {
  const s = (o ?? "").trim().replace(/\/+$/, "");
  return s ? s : null;
}

/** 配置来源：index.html 里的 /config.js（运行时）优先，其次构建时 VITE_LAN_ORIGIN */
export function configuredLanOrigin(): string | null {
  const w = typeof window !== "undefined" ? window.CSP_LAN_ORIGIN : null;
  return normalizeOrigin(w ?? (import.meta.env.VITE_LAN_ORIGIN as string | undefined) ?? null);
}

/** 探测内网：GET <lan>/api/ping，超时即视为不可达 */
export async function probeLan(origin: string, fetchImpl: FetchLike = fetch, timeoutMs = 1500): Promise<boolean> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await Promise.race([
      fetchImpl(`${origin}/api/ping`, { cache: "no-store", signal: ctrl.signal, mode: "cors" }),
      new Promise<never>((_, rej) => setTimeout(() => rej(new Error("timeout")), timeoutMs + 50)),
    ]);
    if (!res.ok) return false;
    const body = (await res.json()) as { ok?: boolean } | null;
    return !!body?.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/** 内网探测器：每 interval 重探一次，状态变化通知订阅者 */
export class LanMonitor {
  private active: string | null = null;
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private origin: string | null,
    private fetchImpl: FetchLike = (u, i) => fetch(u, i),
    private intervalMs = 60_000,
    private timeoutMs = 1500,
  ) {}

  /** 当前生效的 API 源（"" = 同源/外网） */
  get base(): string {
    return this.active ?? "";
  }

  get mode(): NetMode {
    return this.active ? "lan" : "public";
  }

  async probeOnce(): Promise<NetMode> {
    if (!this.origin) return this.set(null);
    const ok = await probeLan(this.origin, this.fetchImpl, this.timeoutMs);
    return this.set(ok ? this.origin : null);
  }

  private set(next: string | null): NetMode {
    if (next !== this.active) {
      this.active = next;
      for (const l of this.listeners) l();
    }
    return this.mode;
  }

  start(): Promise<NetMode> {
    if (!this.timer && this.origin) this.timer = setInterval(() => void this.probeOnce(), this.intervalMs);
    return this.probeOnce();
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  subscribe(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }
}

export const lan = new LanMonitor(configuredLanOrigin());

/** API 地址（内网时为绝对地址） */
export function apiUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${lan.base}${path}`;
}

/** WebSocket 地址：内网 origin 或当前页面 origin，http→ws */
export function wsUrl(path: string): string {
  const base = lan.base || window.location.origin;
  return `${base.replace(/^http/, "ws")}${path}`;
}

/** 媒体 / 证据 / 下载链接：拼 API 源 + ?token=<短期媒体令牌>（img/iframe/a 无法带 Authorization 头）；
 *  令牌还没取到时返回空串，调用方不渲染 */
export function mediaUrl(path: string | null | undefined, token: string | null): string {
  if (!path) return "";
  if (path.startsWith("data:")) return path;
  if (!token) return "";
  const u = apiUrl(path);
  return `${u}${u.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
}

export function useNetMode(): NetMode {
  return useSyncExternalStore(
    (cb) => lan.subscribe(cb),
    () => lan.mode,
    () => "public" as NetMode,
  );
}
